import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, Sparkles, Bot, RotateCcw, Loader2, Mic, Volume2, VolumeX, Target, Play, Check, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { apiPost, apiGet, apiPut } from './ops_helpers';

const SESSION_KEY = 'advisor_session_id';
const newSessionId = () => `adv-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;

// Render seguro de markdown ligero: **negrita** + saltos de linea + vinetas. Sin HTML crudo.
function renderRich(text) {
  const lines = String(text || '').split('\n');
  return lines.map((line, i) => {
    const isBullet = /^\s*[-*]\s+/.test(line);
    const clean = isBullet ? line.replace(/^\s*[-*]\s+/, '') : line;
    const parts = clean.split(/(\*\*[^*]+\*\*)/g).map((seg, j) =>
      /^\*\*[^*]+\*\*$/.test(seg)
        ? <strong key={j} className="text-zinc-100">{seg.slice(2, -2)}</strong>
        : <span key={j}>{seg}</span>
    );
    if (line.trim() === '') return <div key={i} className="h-2" />;
    if (isBullet) {
      return (
        <div key={i} className="flex gap-2 pl-1">
          <span className="text-emerald-400 mt-0.5">•</span>
          <span>{parts}</span>
        </div>
      );
    }
    return <div key={i}>{parts}</div>;
  });
}

// Texto plano para la lectura por voz: quita **negritas** y viñetas para que
// el sintetizador no lea asteriscos ni guiones.
function toPlainText(text) {
  return String(text || '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/^\s*[-*]\s+/gm, '')
    .trim();
}

// ============================================================================
// Optimizador HL dentro de la burbuja: Recomendar -> (editar) -> Simular -> Aplicar.
// Reusa /advisor/recommend + /backtest + /config. Human-in-the-loop (confirma al aplicar).
// ============================================================================
const HL_DEFAULTS = {
  symbols: ['BTC', 'ETH', 'SOL'], days: 90, ltf_interval: '1h',
  sma_fast: 9, sma_slow: 21, sl_pct: 0.025, rr_ratio: 2.5, be_at_r: 1.67,
  risk_per_trade_pct: 0.01, leverage: 3,
  enable_atr_filter: true, enable_htf_filter: true, enable_funding_filter: true, enable_macro_filter: true,
  enable_regime_filter: false, regime_adx_min: 25,
  enable_rsi_filter: false, rsi_long_min: 50, rsi_long_max: 70, rsi_short_min: 30, rsi_short_max: 50,
  enable_volume_filter: false, volume_min_ratio: 1.0, enable_trailing: false,
};

// Config viva del bot -> parametros de backtest (mismo mapeo que el Backtest Lab).
const cfgToParams = (c) => {
  const p = { ...HL_DEFAULTS };
  if (!c) return p;
  return {
    ...p,
    symbols: c.symbols || p.symbols,
    sma_fast: c.sma_fast ?? p.sma_fast,
    sma_slow: c.sma_slow ?? p.sma_slow,
    sl_pct: c.sl_pct ?? p.sl_pct,
    rr_ratio: c.rr_ratio ?? p.rr_ratio,
    be_at_r: c.trailing_be_at_r ?? p.be_at_r,
    risk_per_trade_pct: c.risk_per_trade_pct ?? p.risk_per_trade_pct,
    leverage: c.leverage ?? p.leverage,
    ltf_interval: c.interval || p.ltf_interval,
    enable_funding_filter: c.enable_funding_filter ?? p.enable_funding_filter,
    enable_macro_filter: c.enable_macro_blackout ?? p.enable_macro_filter,
    enable_htf_filter: c.enable_htf_bias_filter ?? p.enable_htf_filter,
    enable_regime_filter: c.enable_regime_filter ?? false,
    regime_adx_min: c.regime_adx_min ?? p.regime_adx_min,
    enable_rsi_filter: c.enable_rsi_filter ?? false,
    rsi_long_min: c.rsi_long_min ?? p.rsi_long_min,
    rsi_long_max: c.rsi_long_max ?? p.rsi_long_max,
    rsi_short_min: c.rsi_short_min ?? p.rsi_short_min,
    rsi_short_max: c.rsi_short_max ?? p.rsi_short_max,
    enable_volume_filter: c.enable_volume_filter ?? false,
    volume_min_ratio: c.volume_min_ratio ?? p.volume_min_ratio,
    enable_trailing: c.enable_trailing ?? false,
  };
};

// Parametros -> config del bot (mismo mapeo que applyToBot del Backtest Lab).
const paramsToCfg = (p) => ({
  symbols: p.symbols, interval: p.ltf_interval || '1h',
  sma_fast: p.sma_fast, sma_slow: p.sma_slow,
  sl_pct: p.sl_pct, rr_ratio: p.rr_ratio,
  risk_per_trade_pct: p.risk_per_trade_pct, leverage: p.leverage,
  trailing_be_at_r: p.be_at_r,
  enable_funding_filter: p.enable_funding_filter,
  enable_macro_blackout: p.enable_macro_filter,
  enable_htf_bias_filter: p.enable_htf_filter,
  enable_regime_filter: !!p.enable_regime_filter,
  regime_adx_min: p.regime_adx_min,
  enable_rsi_filter: !!p.enable_rsi_filter,
  rsi_long_min: p.rsi_long_min, rsi_long_max: p.rsi_long_max,
  rsi_short_min: p.rsi_short_min, rsi_short_max: p.rsi_short_max,
  enable_volume_filter: !!p.enable_volume_filter,
  volume_min_ratio: p.volume_min_ratio,
  enable_trailing: p.enable_trailing,
});

const AGG = (res, key) => res?.aggregate?.[key];

// Tarjetas que el AGENTE devuelve junto a su respuesta: backtests + propuestas de cambio.
const AgentArtifacts = ({ backtests = [], proposals = [] }) => {
  const [applyState, setApplyState] = useState({});

  const applyProposal = async (prop, idx) => {
    if (!window.confirm(`¿Aplicar esta propuesta al bot ${prop.bot_id} EN VIVO? Se guarda como nueva versión y el bot la aplica en ~60s.`)) return;
    setApplyState((s) => ({ ...s, [idx]: { loading: true } }));
    try {
      const cur = (await apiGet(`/api/ops/config/${prop.bot_id}/admin`))?.active_config?.params || {};
      const finalCfg = { ...cur, ...(prop.config_patch || {}) };
      const res = await apiPut(`/api/ops/config/${prop.bot_id}`, { params: finalCfg, note: 'Aplicado desde Asesor IA (agente)', activate: true });
      setApplyState((s) => ({ ...s, [idx]: { ok: true, text: `Aplicado (v${res?.config?.version ?? '?'}). Se refleja en ~60s.` } }));
    } catch (e) {
      setApplyState((s) => ({ ...s, [idx]: { ok: false, text: `Error: ${e?.message || 'desconocido'}` } }));
    }
  };

  const ROWS = [['Trades', 'trades', ''], ['Win Rate', 'win_rate_pct', '%'], ['ROI', 'roi_pct', '%'], ['PnL', 'net_pnl_usd', '$']];
  const fmt = (x, unit) => (x === undefined || x === null) ? '—' : `${unit === '$' ? '$' : ''}${x}${unit === '%' ? '%' : ''}`;

  return (
    <div className="flex w-full max-w-[85%] flex-col gap-2 self-start" data-testid="agent-artifacts">
      {backtests.map((bt, i) => (
        <div key={`bt${i}`} className="rounded-xl border border-cyan-900/60 bg-cyan-950/20 p-2" data-testid="agent-backtest-card">
          <div className="mb-1 text-[11px] font-semibold text-cyan-200">Simulación {bt.bot_id} · {bt.days}d</div>
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-zinc-400">
                <th className="px-1 text-left">Métrica</th>
                <th className="px-1 text-right">Actual</th>
                <th className="px-1 text-right text-cyan-300">Con cambios</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map(([label, key, unit]) => {
                const a = bt.actual?.[key]; const b = bt.recomendada?.[key];
                const better = (typeof a === 'number' && typeof b === 'number' && key !== 'trades') ? (b >= a) : null;
                return (
                  <tr key={key} className="border-t border-zinc-800/60">
                    <td className="px-1 py-0.5 text-zinc-300">{label}</td>
                    <td className="px-1 py-0.5 text-right font-mono text-zinc-300">{fmt(a, unit)}</td>
                    <td className={`px-1 py-0.5 text-right font-mono ${better === true ? 'text-emerald-300' : better === false ? 'text-rose-300' : 'text-cyan-200'}`}>{fmt(b, unit)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      {proposals.map((p, i) => {
        const st = applyState[i] || {};
        return (
          <div key={`pr${i}`} className="rounded-xl border border-violet-900/60 bg-violet-950/20 p-2" data-testid="agent-proposal-card">
            <div className="mb-1 text-[11px] font-semibold text-violet-200">Propuesta de cambio · {p.bot_id}</div>
            <div className="flex flex-wrap gap-1">
              {Object.entries(p.changes || {}).map(([k, v]) => (
                <span key={k} className="rounded border border-violet-800 bg-zinc-800 px-1.5 py-0.5 font-mono text-[10px] text-violet-200" data-testid={`agent-change-${k}`}>
                  {k}: <b className="text-violet-100">{String(v)}</b>
                </span>
              ))}
            </div>
            {p.reason && <p className="mt-1 text-[10px] text-zinc-400">{p.reason}</p>}
            <Button onClick={() => applyProposal(p, i)} disabled={st.loading || st.ok}
              data-testid="agent-apply-btn" className="mt-1.5 h-7 bg-emerald-600 text-[11px] text-white hover:bg-emerald-500">
              {st.loading ? 'Aplicando…' : st.ok ? 'Aplicado ✓' : 'Aplicar al bot'}
            </Button>
            {st.text && <p className={`mt-1 text-[10px] ${st.ok ? 'text-emerald-300' : 'text-rose-300'}`} data-testid="agent-apply-msg">{st.text}</p>}
          </div>
        );
      })}
    </div>
  );
};

const AdvisorOptimizer = () => {
  const [phase, setPhase] = useState('idle'); // idle | loading | ready
  const [err, setErr] = useState(null);
  const [analysis, setAnalysis] = useState('');
  const [recKeys, setRecKeys] = useState([]);
  const [liveCfg, setLiveCfg] = useState(null);
  const [base, setBase] = useState(null);
  const [edit, setEdit] = useState(null);
  const [btRunning, setBtRunning] = useState(false);
  const [bt, setBt] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applyMsg, setApplyMsg] = useState(null);

  const recommend = async () => {
    setPhase('loading'); setErr(null); setBt(null); setApplyMsg(null);
    try {
      let lc = null;
      try { lc = (await apiGet('/api/ops/config/hyperliquid/admin'))?.active_config || null; } catch (e) { /* sin config viva */ }
      setLiveCfg(lc);
      const baseParams = cfgToParams(lc?.params);
      setBase(baseParams);
      const rec = await apiPost('/api/ops/advisor/recommend', { bot_id: 'hyperliquid', current_params: baseParams });
      const recParams = rec?.recommended_params || {};
      setRecKeys(Object.keys(recParams));
      setAnalysis(rec?.analysis || '');
      setEdit({ ...baseParams, ...recParams });
      setPhase('ready');
    } catch (e) {
      setErr(e?.message || 'Error'); setPhase('idle');
    }
  };

  const setField = (k, v) => setEdit((p) => ({ ...p, [k]: v }));

  const simulate = async () => {
    if (!base || !edit) return;
    setBtRunning(true); setBt(null); setErr(null);
    try {
      const mk = (p) => apiPost('/api/ops/backtest', { bot_id: 'hyperliquid', params: { ...p, days: 90 } });
      const [actual, recomendada] = await Promise.all([mk(base), mk(edit)]);
      setBt({ actual, recomendada });
    } catch (e) {
      setErr(e?.message || 'Error en backtest');
    } finally { setBtRunning(false); }
  };

  const applyNow = async () => {
    if (!edit) return;
    if (!window.confirm('¿Aplicar estos parámetros al bot Hyperliquid EN VIVO? Se guarda como nueva versión y el bot la aplica en ~60s.')) return;
    setApplying(true); setApplyMsg(null);
    try {
      const cfgFull = paramsToCfg(edit);
      const cfgBase = paramsToCfg(base);
      const diff = {};
      Object.keys(cfgFull).forEach((k) => {
        if (JSON.stringify(cfgFull[k]) !== JSON.stringify(cfgBase[k])) diff[k] = cfgFull[k];
      });
      const finalCfg = { ...(liveCfg?.params || {}), ...diff };
      const res = await apiPut('/api/ops/config/hyperliquid', { params: finalCfg, note: 'Aplicado desde Asesor IA (burbuja)', activate: true });
      setApplyMsg({ ok: true, text: `Aplicado al bot (v${res?.config?.version ?? '?'}). Se refleja en ~60s.` });
    } catch (e) {
      setApplyMsg({ ok: false, text: `Error al aplicar: ${e?.message || 'desconocido'}` });
    } finally { setApplying(false); }
  };

  const Num = ({ k, label, step }) => (
    <label className="flex flex-col gap-0.5">
      <span className="flex items-center gap-1 text-[10px] text-zinc-400">
        {label}{recKeys.includes(k) && <span className="rounded bg-violet-700/60 px-1 text-[8px] text-violet-100">IA</span>}
      </span>
      <input type="number" step={step || 'any'} value={edit[k]} onChange={(e) => setField(k, parseFloat(e.target.value) || 0)}
        data-testid={`optz-${k}`} className="h-7 w-full rounded border border-zinc-700 bg-zinc-900 px-1.5 text-xs text-zinc-100 focus:border-violet-500/60 focus:outline-none" />
    </label>
  );
  const Chk = ({ k, label }) => (
    <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-zinc-300">
      <input type="checkbox" checked={!!edit[k]} onChange={(e) => setField(k, e.target.checked)} data-testid={`optz-${k}`} className="accent-violet-500" />
      {label}{recKeys.includes(k) && <span className="rounded bg-violet-700/60 px-1 text-[8px] text-violet-100">IA</span>}
    </label>
  );

  return (
    <div data-testid="advisor-optimizer" className="rounded-xl border border-violet-800/60 bg-violet-950/20 p-3">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-violet-200">
        <Target className="h-4 w-4" /> Optimizar Hyperliquid
      </div>

      {phase === 'idle' && (
        <>
          <p className="mb-2 text-[11px] text-zinc-400">La IA analiza tus datos reales y propone parámetros. Tú los editas, simulas y aplicas (con confirmación).</p>
          <Button data-testid="optz-recommend-btn" onClick={recommend} className="h-8 w-full bg-violet-600 text-xs text-white hover:bg-violet-500">
            <Sparkles className="mr-1 h-3.5 w-3.5" /> Recomendar y simular (HL)
          </Button>
          {err && <p className="mt-2 text-[11px] text-rose-300" data-testid="optz-error">{err}</p>}
        </>
      )}

      {phase === 'loading' && (
        <div className="flex items-center gap-2 py-3 text-xs text-zinc-400" data-testid="optz-loading">
          <Loader2 className="h-4 w-4 animate-spin text-violet-400" /> Analizando datos del bot…
        </div>
      )}

      {phase === 'ready' && edit && (
        <div className="space-y-2.5">
          {analysis && <div className="max-h-28 overflow-y-auto text-[11px] leading-relaxed text-zinc-300">{renderRich(analysis)}</div>}

          <div className="grid grid-cols-2 gap-2 rounded-lg border border-zinc-800 bg-zinc-900/40 p-2">
            <Chk k="enable_regime_filter" label="Régimen ADX" />
            {edit.enable_regime_filter && <Num k="regime_adx_min" label="ADX mín" />}
            <Chk k="enable_volume_filter" label="Volumen" />
            {edit.enable_volume_filter && <Num k="volume_min_ratio" label="Vol. ratio" />}
            <Chk k="enable_rsi_filter" label="RSI" />
            <div />
            <Num k="sl_pct" label="SL %" step="0.001" />
            <Num k="rr_ratio" label="RR" step="0.1" />
            <Num k="risk_per_trade_pct" label="Riesgo/op" step="0.001" />
          </div>

          {recKeys.length > 0 && (
            <div className="flex flex-wrap gap-1" data-testid="optz-rec-chips">
              {recKeys.map((k) => (
                <span key={k} className="rounded border border-violet-800 bg-zinc-800 px-1.5 py-0.5 font-mono text-[9px] text-violet-200" data-testid={`optz-chip-${k}`}>
                  {k}: {String(base?.[k])} → <b className="text-violet-100">{String(edit[k])}</b>
                </span>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <Button data-testid="optz-simulate-btn" onClick={simulate} disabled={btRunning}
              className="h-8 border border-cyan-700 bg-transparent text-xs text-cyan-200 hover:bg-cyan-900/40">
              {btRunning ? <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> Simulando…</> : <><Play className="mr-1 h-3.5 w-3.5" /> Simular (90d)</>}
            </Button>
            <Button data-testid="optz-apply-btn" onClick={applyNow} disabled={applying}
              className="h-8 bg-emerald-600 text-xs text-white hover:bg-emerald-500">
              {applying ? <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> Aplicando…</> : <><Check className="mr-1 h-3.5 w-3.5" /> Aplicar al bot</>}
            </Button>
          </div>

          {bt && (
            <div className="overflow-hidden rounded border border-zinc-800" data-testid="optz-delta">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="bg-zinc-900 text-zinc-400">
                    <th className="px-2 py-1 text-left">Métrica</th>
                    <th className="px-2 py-1 text-right">Actual</th>
                    <th className="px-2 py-1 text-right text-violet-300">Recomendada</th>
                  </tr>
                </thead>
                <tbody>
                  {[['Trades', 'trades', ''], ['Win Rate', 'win_rate_pct', '%'], ['ROI', 'roi_pct', '%'], ['PnL neto', 'net_pnl_usd', '$']].map(([label, key, unit]) => {
                    const a = AGG(bt.actual, key); const b = AGG(bt.recomendada, key);
                    const better = (typeof a === 'number' && typeof b === 'number' && key !== 'trades') ? (b >= a) : null;
                    const fmt = (x) => (x === undefined || x === null) ? '—' : `${unit === '$' ? '$' : ''}${x}${unit === '%' ? '%' : ''}`;
                    return (
                      <tr key={key} className="border-t border-zinc-800">
                        <td className="px-2 py-1 text-zinc-300">{label}</td>
                        <td className="px-2 py-1 text-right font-mono text-zinc-300">{fmt(a)}</td>
                        <td className={`px-2 py-1 text-right font-mono ${better === true ? 'text-emerald-300' : better === false ? 'text-rose-300' : 'text-violet-200'}`}>{fmt(b)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {err && <p className="text-[11px] text-rose-300" data-testid="optz-error">{err}</p>}
          {applyMsg && (
            <p data-testid="optz-apply-msg" className={`flex items-center gap-1 text-[11px] ${applyMsg.ok ? 'text-emerald-300' : 'text-rose-300'}`}>
              {applyMsg.ok ? <Check className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />} {applyMsg.text}
            </p>
          )}

          <button onClick={recommend} className="text-[10px] text-zinc-500 underline hover:text-zinc-300" data-testid="optz-redo">Volver a pedir recomendación</button>
        </div>
      )}
    </div>
  );
};

export const AdvisorChat = () => {
  const [open, setOpen] = useState(false);
  const [showOptimizer, setShowOptimizer] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    const s = localStorage.getItem(SESSION_KEY);
    if (s) return s;
    const ns = newSessionId();
    localStorage.setItem(SESSION_KEY, ns);
    return ns;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [listening, setListening] = useState(false);
  const [speak, setSpeak] = useState(() => {
    try { return localStorage.getItem('advisor_tts') === '1'; } catch (e) { return false; }
  });
  const scrollRef = useRef(null);
  const recognitionRef = useRef(null);
  const baseInputRef = useRef('');

  // Reconocimiento de voz nativo del navegador (Web Speech API). Sin backend,
  // sin clave y sin dependencia de Emergent. Requiere HTTPS (producción lo es).
  const sttSupported = typeof window !== 'undefined'
    && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  // Síntesis de voz nativa (lectura en alto de las respuestas). También gratis y local.
  const ttsSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

  // Lee un texto en voz alta (es-ES). Cancela cualquier lectura en curso.
  const speakText = useCallback((text) => {
    if (!ttsSupported) return;
    const plain = toPlainText(text);
    if (!plain) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(plain);
      u.lang = 'es-ES';
      u.rate = 1.0;
      window.speechSynthesis.speak(u);
    } catch (e) { /* noop */ }
  }, [ttsSupported]);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, []);

  // Cargar historial + sugerencias al abrir por primera vez
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [hist, sug] = await Promise.all([
          apiGet(`/api/ops/advisor/history?session_id=${encodeURIComponent(sessionId)}`),
          suggestions.length ? Promise.resolve({ suggestions }) : apiGet('/api/ops/advisor/suggestions'),
        ]);
        if (cancelled) return;
        if (Array.isArray(hist?.messages) && hist.messages.length) setMessages(hist.messages);
        if (Array.isArray(sug?.suggestions)) setSuggestions(sug.suggestions);
        scrollToBottom();
      } catch (e) {
        // historial no critico
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(scrollToBottom, [messages, loading, scrollToBottom]);

  const send = useCallback(async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    // Si el dictado por voz está activo, lo detenemos al enviar.
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) { /* noop */ }
    }
    setListening(false);
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: msg, ts: new Date().toISOString() }]);
    setLoading(true);
    try {
      const res = await apiPost('/api/ops/advisor/agent', { session_id: sessionId, message: msg });
      if (res?.session_id && res.session_id !== sessionId) {
        setSessionId(res.session_id);
        localStorage.setItem(SESSION_KEY, res.session_id);
      }
      const reply = res?.reply || '(sin respuesta)';
      setMessages((prev) => [...prev, {
        role: 'assistant', content: reply, ts: res?.ts,
        proposals: res?.proposals || [], backtests: res?.backtests || [],
      }]);
      if (speak) speakText(reply);
    } catch (e) {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: `No pude responder ahora mismo. ${e?.message || 'Error de conexión con el asesor.'}`,
        error: true,
      }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, sessionId, speak, speakText]);

  const resetConversation = useCallback(() => {
    try { window.speechSynthesis?.cancel(); } catch (e) { /* noop */ }
    const ns = newSessionId();
    localStorage.setItem(SESSION_KEY, ns);
    setSessionId(ns);
    setMessages([]);
  }, []);

  const toggleSpeak = useCallback(() => {
    setSpeak((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('advisor_tts', next ? '1' : '0');
        if (!next) window.speechSynthesis?.cancel();
      } catch (e) { /* noop */ }
      return next;
    });
  }, []);

  // === Dictado por voz (Web Speech API) ===
  const stopListening = useCallback(() => {
    try { recognitionRef.current?.stop(); } catch (e) { /* noop */ }
    setListening(false);
  }, []);

  const toggleListening = useCallback(() => {
    if (!sttSupported || loading) return;
    if (listening) { stopListening(); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = 'es-ES';
    rec.interimResults = true;
    rec.continuous = false;
    rec.maxAlternatives = 1;
    // Conservamos lo ya escrito y le añadimos lo dictado.
    baseInputRef.current = input ? input.trim() + ' ' : '';
    rec.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setInput((baseInputRef.current + transcript).slice(0, 2000));
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch (e) {
      setListening(false);
    }
  }, [sttSupported, loading, listening, input, stopListening]);

  // Limpieza al desmontar: aborta reconocimiento y detiene cualquier lectura.
  useEffect(() => () => {
    try { recognitionRef.current?.abort(); } catch (e) { /* noop */ }
    try { window.speechSynthesis?.cancel(); } catch (e) { /* noop */ }
  }, []);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Boton flotante */}
      {!open && (
        <button
          data-testid="advisor-toggle-button"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-3.5 text-sm font-semibold text-zinc-950 shadow-lg shadow-emerald-500/30 transition-all hover:bg-emerald-400 hover:scale-105 active:scale-95"
        >
          <Sparkles className="h-5 w-5" />
          <span className="hidden sm:inline">Asesor IA</span>
        </button>
      )}

      {/* Panel de chat */}
      {open && (
        <div
          data-testid="advisor-panel"
          className="fixed bottom-6 right-6 z-50 flex h-[min(70vh,560px)] w-[min(92vw,400px)] flex-col overflow-hidden rounded-2xl border border-zinc-700 bg-zinc-900 shadow-2xl shadow-black/50"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-zinc-800 bg-zinc-950/60 px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/15">
                <Bot className="h-5 w-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-zinc-100">Asesor IA de Bots</p>
                <p className="text-[11px] text-zinc-500">Claude · datos en vivo</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="advisor-optimizer-toggle"
                onClick={() => setShowOptimizer((v) => !v)}
                title="Optimizar Hyperliquid (recomendar · simular · aplicar)"
                aria-pressed={showOptimizer}
                className={[
                  'rounded-md p-1.5 transition-colors hover:bg-zinc-800',
                  showOptimizer ? 'text-violet-400' : 'text-zinc-400 hover:text-zinc-200',
                ].join(' ')}
              >
                <Target className="h-4 w-4" />
              </button>
              {ttsSupported && (
                <button
                  data-testid="advisor-tts-toggle"
                  onClick={toggleSpeak}
                  title={speak ? 'Voz activada: leo las respuestas' : 'Voz desactivada'}
                  aria-label={speak ? 'Desactivar lectura en voz alta' : 'Activar lectura en voz alta'}
                  aria-pressed={speak}
                  className={[
                    'rounded-md p-1.5 transition-colors hover:bg-zinc-800',
                    speak ? 'text-emerald-400' : 'text-zinc-400 hover:text-zinc-200',
                  ].join(' ')}
                >
                  {speak ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                </button>
              )}
              <button
                data-testid="advisor-reset-button"
                onClick={resetConversation}
                title="Nueva conversación"
                className="rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              <button
                data-testid="advisor-close-button"
                onClick={() => setOpen(false)}
                title="Cerrar"
                className="rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Mensajes */}
          <div ref={scrollRef} data-testid="advisor-messages" className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {showOptimizer && <AdvisorOptimizer />}
            {messages.length === 0 && !loading && (
              <div className="space-y-3">
                <div className="rounded-xl bg-zinc-800/50 p-3 text-sm text-zinc-300">
                  Hola 👋 Soy tu asesor. Pregúntame sobre el estado o rendimiento de tus bots.
                </div>
                <div className="flex flex-col gap-2">
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      data-testid={`advisor-suggestion-${i}`}
                      onClick={() => send(s)}
                      className="rounded-lg border border-zinc-700 bg-zinc-800/40 px-3 py-2 text-left text-xs text-zinc-300 transition-colors hover:border-emerald-500/50 hover:text-zinc-100"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className="flex flex-col gap-1.5">
                <div
                  data-testid={`advisor-message-${m.role}`}
                  className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}
                >
                  <div
                    className={[
                      'max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed',
                      m.role === 'user'
                        ? 'bg-emerald-500 text-zinc-950'
                        : m.error
                          ? 'bg-rose-500/15 text-rose-200 border border-rose-500/30'
                          : 'bg-zinc-800 text-zinc-200',
                    ].join(' ')}
                  >
                    {m.role === 'assistant' ? renderRich(m.content) : m.content}
                  </div>
                </div>
                {m.role === 'assistant' && ((m.backtests && m.backtests.length > 0) || (m.proposals && m.proposals.length > 0)) && (
                  <AgentArtifacts backtests={m.backtests} proposals={m.proposals} />
                )}
              </div>
            ))}

            {loading && (
              <div className="flex justify-start" data-testid="advisor-loading">
                <div className="flex items-center gap-2 rounded-2xl bg-zinc-800 px-3.5 py-2.5 text-sm text-zinc-400">
                  <Loader2 className="h-4 w-4 animate-spin text-emerald-400" />
                  Analizando tus bots…
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-zinc-800 bg-zinc-950/60 p-3">
            <div className="flex items-end gap-2">
              <textarea
                data-testid="advisor-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                placeholder={listening ? 'Escuchando… habla ahora' : 'Pregúntame o pulsa el micro para hablar…'}
                disabled={loading}
                className="max-h-24 min-h-[40px] flex-1 resize-none rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-emerald-500/60 focus:outline-none disabled:opacity-60"
              />
              {sttSupported && (
                <button
                  type="button"
                  data-testid="advisor-mic-button"
                  onClick={toggleListening}
                  disabled={loading}
                  title={listening ? 'Detener dictado' : 'Hablar por voz'}
                  aria-label={listening ? 'Detener dictado' : 'Hablar por voz'}
                  aria-pressed={listening}
                  className={[
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-all disabled:opacity-50',
                    listening
                      ? 'animate-pulse bg-rose-500 text-white shadow-lg shadow-rose-500/40'
                      : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-emerald-400',
                  ].join(' ')}
                >
                  <Mic className="h-4 w-4" />
                </button>
              )}
              <Button
                data-testid="advisor-send-button"
                onClick={() => send()}
                disabled={loading || !input.trim()}
                className="h-10 w-10 shrink-0 rounded-lg bg-emerald-500 p-0 text-zinc-950 hover:bg-emerald-400"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            {listening ? (
              <p data-testid="advisor-listening-hint" className="mt-1.5 flex items-center justify-center gap-1.5 text-center text-[10px] text-rose-300">
                <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-rose-400" />
                Escuchando… pulsa el micro de nuevo para parar
              </p>
            ) : (
              <p className="mt-1.5 text-center text-[10px] text-zinc-600">
                El asesor analiza datos reales. Verifica antes de operar.
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AdvisorChat;
